import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Building2, Users, Activity, TrendingUp, ArrowLeft, Trophy } from "lucide-react";
import { Wordmark } from "../components/Brand.jsx";
import api from "../lib/api.js";

export default function Corporate() {
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    api.get("/corporate/overview").then((r) => setOverview(r.data));
    api.get("/corporate/trend").then((r) => setTrend(r.data.days.map((d) => ({ date: d.date.slice(5), Active: d.active, Passive: d.passive }))));
  }, []);

  if (!overview) return <div className="text-slate-400 p-12">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#0a1822] text-white">
      <header className="border-b border-[#1a2f40] px-6 lg:px-10 py-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Link to="/home" className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="corp-back"><ArrowLeft size={16} /> App</Link>
          <Wordmark />
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Building2 size={16} color="#2fbf71" /> {overview.employer_name}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="text-xs uppercase tracking-widest text-[#2fbf71] font-bold">Corporate Wellness</div>
        <h1 className="ps-heading text-4xl font-bold mt-2">Personify Health Integration</h1>
        <p className="text-slate-400 mt-2">Aggregate engagement and posture metrics for {overview.employer_code}. No personal health data is shared.</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Stat label="Active Users (7d)" value={overview.active_users} icon={Users} color="#2fbf71" tid="stat-active" />
          <Stat label="Total Members" value={overview.total_users} icon={Building2} color="#00B4D8" tid="stat-total" />
          <Stat label="Participation" value={`${overview.participation_rate}%`} icon={TrendingUp} color="#F59E0B" tid="stat-participation" />
          <Stat label="Engagement Score" value={overview.engagement_score} icon={Activity} color="#a78bfa" tid="stat-engagement" />
        </div>

        <div className="grid lg:grid-cols-3 gap-4 mt-6">
          <Stat label="Total Steps" value={overview.total_steps.toLocaleString()} icon={Activity} color="#2fbf71" tid="stat-steps" />
          <Stat label="Total Passive Steps" value={overview.total_passive_steps.toLocaleString()} icon={Activity} color="#00B4D8" tid="stat-passive" />
          <Stat label="Posture Sessions" value={overview.posture_sessions} icon={Trophy} color="#F59E0B" tid="stat-sessions" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-10">
          <div className="ps-card p-5">
            <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">14-Day Step Trend</div>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="ca" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2fbf71" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#2fbf71" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="cb" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00B4D8" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#00B4D8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1a2f40" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#475569" fontSize={11} />
                <YAxis stroke="#475569" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0e1d2a", border: "1px solid #1a2f40", borderRadius: 12 }} />
                <Legend />
                <Area type="monotone" dataKey="Active" stroke="#2fbf71" fill="url(#ca)" strokeWidth={2.5} />
                <Area type="monotone" dataKey="Passive" stroke="#00B4D8" fill="url(#cb)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="ps-card p-5">
            <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">Weekly Activity</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={trend.slice(-7)}>
                <CartesianGrid stroke="#1a2f40" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#475569" fontSize={11} />
                <YAxis stroke="#475569" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0e1d2a", border: "1px solid #1a2f40", borderRadius: 12 }} />
                <Legend />
                <Bar dataKey="Active" fill="#2fbf71" radius={[8, 8, 0, 0]} />
                <Bar dataKey="Passive" fill="#00B4D8" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="mt-10 ps-card p-5">
          <div className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-3">Top Members (anonymized)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {overview.members.map((m, i) => (
              <div key={m.id || m.email || `${m.name}-${i}`} className="ps-card-elev p-4">
                <div className="w-10 h-10 rounded-full bg-[#0e1d2a] border border-[#1a2f40] flex items-center justify-center font-bold">{m.name?.[0]}</div>
                <div className="mt-2 font-semibold text-sm">{m.name.split(" ")[0]} {m.name.split(" ")[1]?.[0] ?? ""}.</div>
                <div className="text-xs text-slate-400 mt-1">{m.streak}d streak {m.is_premium ? "· Premium" : ""}</div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, icon: Icon, color, tid }) {
  return (
    <div className="ps-card p-5" data-testid={tid}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-[#142534] flex items-center justify-center"><Icon size={18} color={color} /></div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">{label}</div>
      </div>
      <div className="ps-heading text-3xl font-bold mt-3">{value}</div>
    </div>
  );
}
